/**
 * Token Replenishment Service
 * 
 * Automatically replenishes user tokens up to their rank limit every hour,
 * even if the user is offline.
 */

import { updateUser, getUser } from './firebaseService';
import { User } from '../types';
import { smartNotificationsService } from './smartNotificationsService';

/**
 * Calculate how many tokens should be replenished based on elapsed time
 * @param user - The user to calculate for
 * @param config - Global config with token limits and refresh rates
 * @returns Updated token balance
 */
export const calculateTokenReplenishment = (
    user: User,
    config: {
        maxTokenLimit?: number;
        premiumTokenLimit?: number;
        freeTokenLimit?: number;
        refreshIntervalMinutes?: number;
        maxRefreshRate?: number;
        premiumRefreshRate?: number;
        standardRefreshRate?: number;
    }
): number => {
    const now = Date.now();
    const lastRefresh = user.lastTokenRefresh || now;
    const refreshInterval = (config?.refreshIntervalMinutes || 60) * 60 * 1000; // 1 hour default

    // Calculate elapsed time in milliseconds
    const elapsedTime = now - lastRefresh;

    // Only replenish if at least one refresh interval has passed
    if (elapsedTime < refreshInterval) {
        return user.tokenBalance || 0;
    }

    // Determine user tier and get appropriate limits
    const isMaxTier = user.isMax || user.accountTier === 'max';
    const isPremium = user.isPremium && !isMaxTier;

    const tokenLimit = isMaxTier
        ? (config?.maxTokenLimit || 50000)  // Max tier: 50k tokens
        : isPremium
            ? (config?.premiumTokenLimit || 15000)  // Premium: 15k tokens
            : (config?.freeTokenLimit || 2000);  // Standard: 2k tokens

    const refreshRate = isMaxTier
        ? (config?.maxRefreshRate || 5000)  // Max refreshes 5k/hr
        : isPremium
            ? (config?.premiumRefreshRate || 1500)  // Premium refreshes 1.5k/hr
            : (config?.standardRefreshRate || 200);  // Standard refreshes 200/hr

    // Calculate how many complete intervals have passed
    const intervalsPassed = Math.floor(elapsedTime / refreshInterval);

    // Add tokens for ALL elapsed intervals (so offline time is counted)
    const tokensToAdd = refreshRate * intervalsPassed;
    const newBalance = Math.min(
        (user.tokenBalance || 0) + tokensToAdd,
        tokenLimit
    );

    console.log(`[TokenReplenishment] User: ${user.username || user.id}`);
    console.log(`  Last refresh: ${new Date(lastRefresh).toLocaleString()}`);
    console.log(`  Elapsed time: ${(elapsedTime / 1000 / 60).toFixed(1)} minutes`);
    console.log(`  Intervals passed: ${intervalsPassed}`);
    console.log(`  Tokens to add: ${tokensToAdd.toLocaleString()}`);
    console.log(`  Current: ${(user.tokenBalance || 0).toLocaleString()} → New: ${newBalance.toLocaleString()}`);

    return newBalance;
};

/**
 * Replenish tokens for a user (called on login or periodically)
 * @param userId - User ID to replenish tokens for
 * @param config - Global config
 * @returns Promise<void>
 */
export const replenishUserTokens = async (
    userId: string,
    config: {
        maxTokenLimit?: number;
        premiumTokenLimit?: number;
        freeTokenLimit?: number;
        refreshIntervalMinutes?: number;
        maxRefreshRate?: number;
        premiumRefreshRate?: number;
        standardRefreshRate?: number;
    }
): Promise<void> => {
    try {
        // Get fresh user data
        const user = await getUser(userId);
        if (!user) {
            console.warn(`[TokenReplenishment] User ${userId} not found`);
            return;
        }

        const now = Date.now();
        const lastRefresh = user.lastTokenRefresh || now;
        const refreshInterval = (config?.refreshIntervalMinutes || 60) * 60 * 1000;
        const elapsedTime = now - lastRefresh;

        // Only replenish if at least one cycle has passed
        if (elapsedTime < refreshInterval) {
            console.log(`[TokenReplenishment] No replenishment needed for ${user.username}`);
            return;
        }

        // Calculate new balance
        const newBalance = calculateTokenReplenishment(user, config);

        // Only update if balance changed
        if (newBalance !== user.tokenBalance) {
            const refreshCycles = Math.floor(elapsedTime / refreshInterval);

            await updateUser(userId, {
                tokenBalance: newBalance,
                lastTokenRefresh: now,
                dailyTokenUsage: 0 // Reset daily usage on replenishment
            });

            console.log(`✅ [TokenReplenishment] Replenished ${user.username}: ${user.tokenBalance?.toLocaleString()} → ${newBalance.toLocaleString()}`);
        }

        // Schedule next refill notification
        const nextRefillTime = (user.lastTokenRefresh || now) + refreshInterval;
        if (nextRefillTime > now) {
            smartNotificationsService.scheduleTokenRefillNotification(nextRefillTime);
        }
    } catch (error) {
        console.error(`[TokenReplenishment] Error replenishing tokens for ${userId}:`, error);
    }
};

/**
 * Check and replenish tokens on user login
 * @param user - User object
 * @param config - Global config
 * @returns Updated user object
 */
export const checkAndReplenishOnLogin = async (
    user: User,
    config: {
        maxTokenLimit?: number;
        premiumTokenLimit?: number;
        freeTokenLimit?: number;
        refreshIntervalMinutes?: number;
        maxRefreshRate?: number;
        premiumRefreshRate?: number;
        standardRefreshRate?: number;
    }
): Promise<User> => {
    const newBalance = calculateTokenReplenishment(user, config);

    // If tokens need replenishment
    if (newBalance !== user.tokenBalance) {
        await replenishUserTokens(user.id, config);

        // Calculate new lastTokenRefresh based on how many intervals passed
        const now = Date.now();
        const lastRefresh = user.lastTokenRefresh || now;
        const refreshInterval = (config?.refreshIntervalMinutes || 60) * 60 * 1000;
        const elapsedTime = now - lastRefresh;
        const intervalsPassed = Math.floor(elapsedTime / refreshInterval);

        // Set lastTokenRefresh to the start of the current interval
        // This ensures the timer shows correct countdown
        const newLastRefresh = lastRefresh + (intervalsPassed * refreshInterval);

        return {
            ...user,
            tokenBalance: newBalance,
            lastTokenRefresh: newLastRefresh,
            dailyTokenUsage: 0
        };
    }

    // Schedule next refill notification
    const now = Date.now();
    const lastRefresh = user.lastTokenRefresh || now;
    const refreshInterval = (config?.refreshIntervalMinutes || 60) * 60 * 1000;
    const nextRefillTime = lastRefresh + refreshInterval;

    if (nextRefillTime > now) {
        smartNotificationsService.scheduleTokenRefillNotification(nextRefillTime);
    }

    return user;
};

export default {
    calculateTokenReplenishment,
    replenishUserTokens,
    checkAndReplenishOnLogin
};
